export type FlowerKind =
  | 'rose'
  | 'bluebell'
  | 'fern'
  | 'sunflower'
  | 'lavender'
  | 'tulip'
  | 'orchid'
  | 'crystal';

export type CarryKind = FlowerKind | 'honey' | 'cash';

export type UpgradeId =
  | 'stack'
  | 'speed'
  | 'bees'
  | 'honeyRate'
  | 'clerk'
  | 'clerkCarry'
  | 'queue'
  | 'fields'
  | 'prices'
  | 'storeroom'
  | 'flowers';

export const FLOWER_ORDER: FlowerKind[] = [
  'rose',
  'bluebell',
  'fern',
  'sunflower',
  'lavender',
  'tulip',
  'orchid',
  'crystal',
];

export const FLOWER_META: Record<
  FlowerKind,
  {
    label: string;
    price: number;
    color: number;
    bushColor: number;
    unlockLevel: number; // flowers upgrade level required (0 = start)
    tier: number;
  }
> = {
  rose: { label: 'Rose', price: 3, color: 0xe4576a, bushColor: 0x2f8a4a, unlockLevel: 0, tier: 1 },
  bluebell: { label: 'Bluebell', price: 5, color: 0x4f8cff, bushColor: 0x2f7a58, unlockLevel: 0, tier: 1 },
  fern: { label: 'Fern', price: 8, color: 0x49c27a, bushColor: 0x1f6b42, unlockLevel: 0, tier: 1 },
  sunflower: { label: 'Sunflower', price: 12, color: 0xffc933, bushColor: 0x3a8f4a, unlockLevel: 1, tier: 2 },
  lavender: { label: 'Lavender', price: 16, color: 0xb48cff, bushColor: 0x2f7a58, unlockLevel: 2, tier: 2 },
  tulip: { label: 'Tulip', price: 22, color: 0xff6b9d, bushColor: 0x2f8a4a, unlockLevel: 3, tier: 3 },
  orchid: { label: 'Orchid', price: 32, color: 0xff9de2, bushColor: 0x1f6b55, unlockLevel: 4, tier: 3 },
  crystal: { label: 'Crystal Bloom', price: 50, color: 0x7ef0ff, bushColor: 0x2a6b7a, unlockLevel: 5, tier: 4 },
};

export const HONEY_COLOR = 0xf0b429;

export type UpgradeDef = {
  id: UpgradeId;
  label: string;
  blurb: string;
  maxLevel: number;
  baseCost: number;
  costScale: number;
  /** human readable effect per level */
  effect: string;
};

export const UPGRADES: UpgradeDef[] = [
  {
    id: 'stack',
    label: 'Bigger Basket',
    blurb: 'Carry a taller flower tower',
    maxLevel: 12,
    baseCost: 40,
    costScale: 1.55,
    effect: '+6 stack capacity',
  },
  {
    id: 'speed',
    label: 'Sprint Sneakers',
    blurb: 'Zip across the gardens faster',
    maxLevel: 10,
    baseCost: 50,
    costScale: 1.6,
    effect: '+8% move speed',
  },
  {
    id: 'bees',
    label: 'Bee Colony',
    blurb: 'More buzzing garden helpers',
    maxLevel: 8,
    baseCost: 70,
    costScale: 1.7,
    effect: '+1 bee',
  },
  {
    id: 'honeyRate',
    label: 'Sweet Nectar',
    blurb: 'Honey jars appear faster',
    maxLevel: 10,
    baseCost: 60,
    costScale: 1.55,
    effect: 'Faster honey drops',
  },
  {
    id: 'clerk',
    label: 'Hire Clerks',
    blurb: 'Extra staff for the storeroom route',
    maxLevel: 3,
    baseCost: 120,
    costScale: 2.1,
    effect: '+1 clerk',
  },
  {
    id: 'clerkCarry',
    label: 'Clerk Carts',
    blurb: 'Staff haul bigger armfuls',
    maxLevel: 8,
    baseCost: 80,
    costScale: 1.6,
    effect: '+3 clerk carry',
  },
  {
    id: 'queue',
    label: 'Shop Buzz',
    blurb: 'Longer lines, richer demand',
    maxLevel: 8,
    baseCost: 90,
    costScale: 1.65,
    effect: '+1 queue slot, faster arrivals',
  },
  {
    id: 'fields',
    label: 'Expand Fields',
    blurb: 'Unlock outer garden rings',
    maxLevel: 5,
    baseCost: 100,
    costScale: 1.85,
    effect: 'New plots + world stretch',
  },
  {
    id: 'prices',
    label: 'Boutique Branding',
    blurb: 'Customers pay more for blooms',
    maxLevel: 10,
    baseCost: 110,
    costScale: 1.7,
    effect: '+10% sale prices',
  },
  {
    id: 'storeroom',
    label: 'Warehouse Shelves',
    blurb: 'Stock more for your clerks',
    maxLevel: 10,
    baseCost: 75,
    costScale: 1.55,
    effect: '+12 storeroom slots',
  },
  {
    id: 'flowers',
    label: 'Rare Catalog',
    blurb: 'Unlock exotic flower species',
    maxLevel: 5,
    baseCost: 150,
    costScale: 2.0,
    effect: 'Unlock next flower tier',
  },
];

export const GAME = {
  playerSpeed: 6.0,
  playerRadius: 0.45,
  baseStackMax: 16,
  stackItemHeight: 0.26,
  harvestRadius: 1.1,
  depositRadius: 1.45,
  sellRadius: 1.3,
  honeyPickupRadius: 1.05,
  cashPickupRadius: 1.15,
  baseStoreroom: 40,
  honeyPerPlant: 1,
  baseBeeCount: 3,
  baseHoneyInterval: 2.6,
  plantFlightSpeed: 5.8,
  baseCustomerInterval: 3.0,
  baseMaxQueue: 5,
  baseEmployeeSpeed: 4.2,
  baseEmployeeCarry: 6,
  baseWorldHalf: 16,
  milestones: [40, 120, 300, 750, 1800, 4000, 9000, 20000, 50000],
  saveKey: 'bloom-basket-save-v2',
} as const;

export const ZONE = {
  counter: { x: 0, z: 8.5 },
  storeroom: { x: -8.5, z: 5.5 },
  hive: { x: 9.2, z: -1.2 },
  upgradeShop: { x: 7.5, z: 6.8 },
  spawn: { x: 0, z: 3.5 },
  gardenCenter: { x: 0, z: -6 },
  moneyPad: { x: 2.2, z: 7.2 },
} as const;

export function upgradeCost(def: UpgradeDef, level: number): number {
  return Math.floor(def.baseCost * Math.pow(def.costScale, level));
}

export function unlockedFlowers(flowersLevel: number): FlowerKind[] {
  return FLOWER_ORDER.filter((k) => FLOWER_META[k].unlockLevel <= flowersLevel);
}

export function salePrice(kind: FlowerKind, pricesLevel: number): number {
  const base = FLOWER_META[kind].price;
  return Math.max(1, Math.round(base * (1 + pricesLevel * 0.1)));
}
